import { Tables } from '../../db_types'
import { Deals } from '../../custom_types'

export const DEAL_SPLIT_CHILDREN_SELECT =
  'split_children:deals!split_anchor_id(*, profiles(id, img_url, name, email))'

export type DealProfileSummary = Pick<Tables<'profiles'>, 'id' | 'img_url' | 'name' | 'email'>

export type SplitChildDeal = Deals & {
  profiles?: DealProfileSummary | null
}

export type SplitAnchorDealReference = Pick<Tables<'deals'>, 'id' | 'deal_id' | 'name'>

export type SplitAwareDeal = Deals & {
  split_children?: SplitChildDeal[] | null
  split_anchor?: Tables<'deals'> | null
}

export type SplitAwareDealReference = Pick<
  Tables<'deals'>,
  | 'id'
  | 'deal_id'
  | 'name'
  | 'split_record_type'
  | 'split_anchor_id'
  | 'split_percentage'
  | 'split_original_amount'
> & {
  split_anchor?: SplitAnchorDealReference | null
}

const compareNullableStrings = (left: string | null, right: string | null) =>
  (left ?? '').localeCompare(right ?? '', undefined, { sensitivity: 'base' })

function getDealMetaValue(deal: Deals, fieldName: string) {
  const meta = deal.meta

  if (meta == null) return null

  if (typeof meta === 'string') {
    try {
      const parsed = JSON.parse(meta) as Record<string, unknown>
      return parsed[fieldName] ?? null
    } catch {
      return null
    }
  }

  if (typeof meta === 'object' && !Array.isArray(meta)) {
    return (meta as Record<string, unknown>)[fieldName] ?? null
  }

  return null
}

function dealMatchesUserField(deal: Deals, profileId: string, customUserFields: string[]) {
  if (deal.owner_id === profileId) {
    return true
  }

  return customUserFields.some(fieldName => String(getDealMetaValue(deal, fieldName)) === profileId)
}

export function isSplitChildDeal(deal: Pick<Deals, 'split_record_type'> | null | undefined) {
  return deal?.split_record_type === 'split_child'
}

export function isVisibleTopLevelDeal(deal: Pick<Deals, 'split_record_type'> | null | undefined) {
  return deal?.split_record_type !== 'split_child'
}

export function getSplitChildDeals(deal: SplitAwareDeal | null | undefined) {
  return [...(deal?.split_children ?? [])]
    .filter(child => child.split_record_type === 'split_child')
    .sort((left, right) => {
      const percentageDelta = (right.split_percentage ?? 0) - (left.split_percentage ?? 0)

      if (percentageDelta !== 0) {
        return percentageDelta
      }

      return compareNullableStrings(
        left.profiles?.name ?? left.deal_id ?? left.name,
        right.profiles?.name ?? right.deal_id ?? right.name
      )
    })
}

export function hasDealSplitChildren(deal: SplitAwareDeal | null | undefined) {
  return getSplitChildDeals(deal).length > 0
}

export function isSplitAwareOwnerField(
  source: string | null | undefined,
  userField: string | null | undefined
) {
  if (!source || !userField) {
    return false
  }

  if (source === 'deal') {
    return userField === 'owner_id'
  }

  return (
    (source === 'activity' ||
      source === 'invoice' ||
      source === 'line_item' ||
      source === 'deal_line_item') &&
    userField === 'deal.owner_id'
  )
}

export function getVisibleDealId(
  deal: Pick<Deals, 'id' | 'split_record_type' | 'split_anchor_id'>
) {
  return deal.split_record_type === 'split_child' ? (deal.split_anchor_id ?? deal.id) : deal.id
}

export function getVisibleDeal(deal: SplitAwareDeal | null | undefined) {
  if (deal?.split_record_type === 'split_child' && deal.split_anchor != null) {
    return deal.split_anchor
  }

  return deal ?? null
}

export function getVisibleDealReference(
  deal: SplitAwareDealReference | null | undefined
): SplitAnchorDealReference | null {
  if (!deal) {
    return null
  }

  if (deal.split_record_type === 'split_child' && deal.split_anchor != null) {
    return deal.split_anchor
  }

  return {
    id: deal.id,
    deal_id: deal.deal_id,
    name: deal.name
  }
}

export function getDealMatchCandidates(deal: SplitAwareDeal) {
  const splitChildren = getSplitChildDeals(deal)

  if (deal.split_record_type === 'anchor' && splitChildren.length > 0) {
    return splitChildren
  }

  return [deal]
}

export function visibleDealMatchesProfile(
  deal: SplitAwareDeal,
  profileId: string,
  customUserFields: string[]
) {
  return getDealMatchCandidates(deal).some(candidate =>
    dealMatchesUserField(candidate, profileId, customUserFields)
  )
}

export function filterVisibleDealsByProfile(
  deals: SplitAwareDeal[],
  profileId: string,
  customUserFields: string[]
) {
  return deals.filter(
    deal =>
      isVisibleTopLevelDeal(deal) && visibleDealMatchesProfile(deal, profileId, customUserFields)
  )
}
